<template>
    <ui-modal>
        <template #header>
            <h3 v-text="'Powiązanie sklepów'" class="mb-0" />
            <h5 v-if="hasInvalid && !isLoading" v-text="'Informacja'" class="text-danger text-center mb-0" />
            <h5 v-else v-text="'Podsumowanie'" class="text-center mb-0" />
        </template>

        <div v-if="!isLoading">
            <div
                v-if="!hasInvalid"
                v-text="`UWAGA! Sprawdź poprawność powiązania sklepów. Jeśli chcesz zmienić wybór,
                    wróć do poprzedniego kroku. Późniejsze zmiany będą możliwe jedynie przez kontakt z HelpDesk.`"
                class="a-infoBar a-infoBar--warning text-sm mb-3"
            />

            <div v-else>
                <div class="text-center p-4">
                    <a-icon
                        class="text-warning mb-4"
                        width="40px"
                        height="40px"
                        icon="WarningFull"
                    />
                    <h5
                        v-text="'Uwaga! Zauważyliśmy brak zgodności danych w poniższych sklepach.'"
                        class="font-weight-normal text-danger mt-3"
                    />
                    <h5
                        v-text="'Jeśli chcesz zmienić wybór sklepu, wróć do poprzedniego kroku.'"
                        class="font-weight-normal"
                    />
                </div>
            </div>

            <div class="c-table location-mapping-summary-table">
                <div class="c-thead">
                    <div v-text="'Sklep EuroPlatform'" class="c-td is-1" />
                    <div v-text="'Sklep Kaucyjni.pl'" class="c-td is-2" />
                    <div v-text="'Zgodność'" class="c-td is-3" />
                </div>
                <div class="c-tbody">
                    <location-mapping-summary-row
                        v-for="row in mappedLocations"
                        :key="row.epLoc.id"
                        :row="row"
                    />

                    <ui-checkbox-input
                        v-if="hasInvalid"
                        v-model="isConfirmed"
                        class="text-primary text-sm mt-3"
                        name="summary-confirm"
                        :label="`Potwierdzam, że chcę powiązać sklepy bez zgodności danych między systemami EuroPlatform
                            i Kaucyjni.pl. Mam świadomość, że dane sklepu w EuroPlatform zastąpią dane w Kaucyjni.pl.
                            <span class='text-danger'>*</span>
                        `"
                    />
                </div>
            </div>
        </div>

        <div v-else class="text-center">
            <page-preloader
                min-height="10rem"
                loading-status="loading"
                text="Trwa uruchamianie usługi..."
            />
        </div>

        <template v-if="!isLoading" #footer>
            <the-btn
                text="Wróć"
                class="btn-outline-custom"
                @click="goBack"
            />
            <the-btn
                text="Włącz usługę"
                class="btn-secondary"
                :disabled="hasInvalid && !isConfirmed"
                @click="submit"
            />
        </template>
    </ui-modal>
</template>

<script>
import locationMappingMixin, { VALID_DATA_STATUS } from '@/components/drs/service/location-mapping.mixin'
import { registerPageModule } from '@/utils/store'
import DrsStore from '@/store/modules/drs/drs.module'
import DrsTariffs from '@/store/modules/drs/tariffs.module'

export default {
    mixins: [locationMappingMixin],

    provide() {
        return {
            closeModal: this.closeModalAndClearState
        }
    },

    data: () => ({
        isConfirmed: false,
        hasInvalid: false,
        isLoading: false
    }),

    computed: {
        service() {
            return this.$store.getters.getService
        },

        locationsEP() {
            return this.$store.getters.locationsToActivate
        },

        locationsKPL() {
            return this.$store.getters.unMappedDrsLocations
        },

        mappings() {
            return this.$store.getters.drsLocationMappings
        },

        mappedLocations() {
            const replicatedLocations = this.$store.getters.replicatedLocationsByEpId
            const mappedLocations = this.locationsEP.map(epLoc => {
                const replicatedKplLoc = replicatedLocations[epLoc.id]
                const selectedKplId = this.mappings[epLoc.id]
                const selectedKplLoc = this.locationsKPL.find(loc => loc.id === selectedKplId)
                const kplLoc = replicatedKplLoc || selectedKplLoc

                return {
                    epLoc,
                    kplLoc,
                    status: replicatedKplLoc ? null : this.getMappingStatus(epLoc, kplLoc)
                }
            })

            if (this.hasInvalid) {
                return mappedLocations.filter(loc => loc.status === VALID_DATA_STATUS.INVALID)
            }

            return mappedLocations
        },

        isSubmitDisabled() {
            if (!this.hasInvalid) {
                return false
            }

            return !this.isConfirmed
        }
    },

    methods: {
        getMappingStatus(epLoc, kplLoc) {
            if (!kplLoc) {
                return VALID_DATA_STATUS.NONE
            }

            if (!this.areLocationsEqual(epLoc, kplLoc)) {
                return VALID_DATA_STATUS.INVALID
            }

            return VALID_DATA_STATUS.OK
        },

        goBack() {
            if (this.hasInvalid) {
                this.hasInvalid = false
            }

            this.$store.dispatch('openModal', {
                componentPath: 'drs/service/LocationMapping'
            })
        },

        async submit() {
            const hasInvalidMapping = this.mappedLocations.some(
                row => row.status === VALID_DATA_STATUS.INVALID
            )

            if (hasInvalidMapping && !this.hasInvalid) {
                this.hasInvalid = hasInvalidMapping
                return
            }

            // Register drs modules
            await registerPageModule(this.$store, DrsStore, 'drs')
            await registerPageModule(this.$store, DrsTariffs, 'drsTariffs')

            const isDrsAuthorizationRequired = this.service.number_of_entities_with_subscription === 0
            const shopIds = this.$store.getters.activatableDrsLocationIds()

            if (isDrsAuthorizationRequired && shopIds.length) {
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/service/Authorization',
                    args: {
                        shopIds,
                        serviceCode: this.service.code,
                        hasLocationsToMap: true
                    }
                })
            } else {
                this.activateService()
            }
        },

        async activateService() {
            let mappedSubscriptionsSummary = {}

            try {
                this.isLoading = true

                const shopIds = this.$store.getters.activatableDrsLocationIds()

                // Activate service
                if (shopIds.length) {
                    const { status, data } = await this.$store.dispatch('sendServiceActivateRequest', {
                        shopIds,
                        serviceId: this.service.id,
                        level: this.service.level
                    })

                    if (status !== 200) {
                        throw new Error(data.message || 'Failed to activate drs service')
                    }
                }

                // Map locations
                await this.$store.dispatch('mapDrsLocations')

                // Replicate locations
                try {
                    await this.$store.dispatch('replicateDrsLocations', { shopIds })
                } catch (error) {
                    console.error(error)
                }

                // Update subscriptions
                mappedSubscriptionsSummary = await this.$store.dispatch('prepareMappedSubscriptionsSummary')
            } catch (error) {
                console.error('Failed to activate DRS service:', error)
            } finally {
                this.isLoading = false

                this.$store.dispatch('openModal', {
                    componentPath: 'drs/service/Summary',
                    args: {
                        serviceCode: this.service.code,
                        isLocationMappingProcess: true,
                        ...mappedSubscriptionsSummary
                    }
                })
            }
        }
    }
}
</script>